import { Form, FormSelect, FormTextInput, StandardFormButtons } from '@components/form';
import { FormTagsInput } from '@components/form/FormInputs/FormTagsInput';
import { ExternalLink } from '@components/link';
import { Typography } from '@components/typography';
import { useCreateGANDatasetMutation, useGetGANModelsQuery } from '@generated/graphql';
import { MenuItem, Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';
import * as yup from 'yup';

const formInitialValues = {
  name: '',
  description: '',
  modelName: '',
  tags: [],
};
const validationSchema = yup.object({
  name: yup
    .string()
    .trim()
    .max(128, 'Must be less than 128 characters')
    .required('Name is required'),
  modelName: yup.string().trim().required('Model name is required'),
  description: yup.string(),
});

interface DatasetsDetailsGanDatasetsCreateModalProps {
  datasetId: string;
  workspaceId: string;
  onClose: VoidFunction;
  refetch: VoidFunction;
  refetchDatasets: VoidFunction;
}

export const DatasetsDetailsGanDatasetsCreateModal = ({
  onClose,
  datasetId,
  workspaceId,
  refetch,
  refetchDatasets,
}: DatasetsDetailsGanDatasetsCreateModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const { data, loading: ganModelsLoading } = useGetGANModelsQuery({ variables: { workspaceId } });
  const [createGANDataset] = useCreateGANDatasetMutation();

  const sortedGanModels = [...(data?.getGANModels || [])].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );
  const ganModelsModelIds = sortedGanModels.map((m) => m.modelId);

  const handleCreateGanDataset = createAsyncNotification(
    'Successfully created GAN Dataset.',
    async ({
      modelName,
      name,
      description,
      tags,
    }: {
      modelName: string;
      name: string;
      description: string;
      tags: string[];
    }) => {
      await createGANDataset({
        variables: { datasetId, modelId: modelName, workspaceId, name, description, tags },
      });
      posthog.capture('user_create-gan-dataset_submitted');
      if (refetch) {
        refetch();
      }
      if (refetchDatasets) {
        refetchDatasets();
      }
      onClose();
    },
  );

  if (ganModelsLoading) return <Typography variant="body1">Loading...</Typography>;

  return (
    <Form
      validateOnBlur={false}
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleCreateGanDataset}
    >
      <Stack>
        <FormTextInput label="Name" name="name" />
        <FormSelect label="Model" name="modelName" placeholder="select">
          {sortedGanModels.length > 0 ? (
            ganModelsModelIds.map((option) => (
              <MenuItem key={option} value={option}>
                {sortedGanModels.find((m) => m.modelId === option).name}
              </MenuItem>
            ))
          ) : (
            <MenuItem value="">Your workspace has no GAN Models. Contact your admin.</MenuItem>
          )}
        </FormSelect>
        <FormTextInput label="Description" name="description" multiline rows={3} />
        <FormTagsInput name="tags" />
        <StandardFormButtons onClose={onClose} />
      </Stack>
    </Form>
  );
};

export const DatasetsDetailsGanDatasetsCreateModalHelpContent = (
  <Typography variant="body2">
    Domain adaptation is a technique used to bridge the gap between real and synthetic domains when
    generating synthetic datasets. Because our imagery is computer generated, it may lack some of
    the characteristics that make-up real imagery. We use Generative Adversarial Networks or GANs as
    a tool to help us bridge that gap by training our GAN on real imagery then applying that GAN to
    the synthetic dataset. We use{' '}
    <ExternalLink href="https://junyanz.github.io/CycleGAN/">CycleGAN</ExternalLink> to accomplish
    this. CycleGAN is an image-to-image translation project shared at ICCV 2017. References for
    training a CycleGAN model can be found on the{' '}
    <ExternalLink href="https://github.com/junyanz/pytorch-CycleGAN-and-pix2pix#getting-started">
      project site
    </ExternalLink>
    .
  </Typography>
);
